import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, ResponsiveContainer, Cell, ReferenceLine
} from 'recharts';
import { formatCurrency } from '../../utils/formatters';
import { API_BASE_URL } from '../../config';
import './embed.css';

const EmbedRandomDetail = () => {
  const [searchParams] = useSearchParams();

  const bg = searchParams.get('bg') || '#0a0a0a';
  const rotateInterval = parseInt(searchParams.get('rotate')) || 60000;
  const refreshInterval = parseInt(searchParams.get('refresh')) || 5000;
  const variant = searchParams.get('variant') || 'full'; // full, chart, ceo, price

  // What to show per variant
  const variants = {
    full:  { company: true, ceo: true, chart: true, price: true, stats: true, desc: true },
    chart: { company: true, ceo: false, chart: true, price: true, stats: false, desc: false },
    ceo:   { company: true, ceo: true, chart: false, price: true, stats: false, desc: true },
    price: { company: true, ceo: false, chart: false, price: true, stats: true, desc: false },
  };
  const show = variants[variant] || variants.full;

  const [allTickers, setAllTickers] = useState([]);
  const [currentTicker, setCurrentTicker] = useState(null);
  const [stockData, setStockData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [fadeClass, setFadeClass] = useState('embed-fade-in');
  const socketRef = useRef(null);

  // Fetch all tickers once
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/stocks`)
      .then(r => r.json())
      .then(stocks => {
        const tickers = stocks.map(s => s.ticker);
        setAllTickers(tickers);
        if (tickers.length > 0) {
          setCurrentTicker(tickers[Math.floor(Math.random() * tickers.length)]);
        }
      })
      .catch(e => console.error(e));
  }, []);

  // Rotate ticker
  useEffect(() => {
    if (allTickers.length === 0) return;
    const timer = setInterval(() => {
      setFadeClass('embed-fade-out');
      setTimeout(() => {
        setCurrentTicker(allTickers[Math.floor(Math.random() * allTickers.length)]);
        setFadeClass('embed-fade-in');
      }, 400);
    }, rotateInterval);
    return () => clearInterval(timer);
  }, [allTickers, rotateInterval]);

  // Fetch data when ticker changes + polling fallback
  useEffect(() => {
    if (!currentTicker) return;

    const fetchData = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/stocks/ticker/${currentTicker}`);
        if (res.ok) {
          const stock = await res.json();
          setStockData(stock);

          if (show.chart) {
            const histRes = await fetch(`${API_BASE_URL}/api/stocks/${stock.id}/history?interval=1m&limit=60`);
            if (histRes.ok) {
              const hist = await histRes.json();
              setChartData((hist.data || []).map((c, i) => ({
                index: i,
                time: new Date(c.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                open: c.open, high: c.high, low: c.low, close: c.close, volume: c.volume,
              })));
            }
          }
        }
      } catch (e) { console.error(e); }
    };

    fetchData();
    const interval = setInterval(fetchData, refreshInterval);
    return () => clearInterval(interval);
  }, [currentTicker, refreshInterval]);

  // Socket for live prices
  useEffect(() => {
    socketRef.current = io(API_BASE_URL, { auth: { public: true } });
    socketRef.current.on('stock-updates', (updates) => {
      const arr = Array.isArray(updates) ? updates : updates?.stocks;
      if (!arr) return;
      setStockData(prev => {
        if (!prev) return prev;
        const match = arr.find(u => u.ticker?.toUpperCase() === prev.ticker?.toUpperCase());
        if (!match) return prev;
        const livePrice = parseFloat(match.currentPrice || match.price);
        if (!livePrice || isNaN(livePrice)) return prev;
        const basePrice = prev.basePrice || prev.currentPrice;
        return {
          ...prev,
          currentPrice: livePrice,
          change24h: parseFloat((livePrice - basePrice).toFixed(4)),
          changePercent24h: parseFloat((basePrice > 0 ? ((livePrice - basePrice) / basePrice) * 100 : 0).toFixed(2)),
        };
      });
    });
    return () => { if (socketRef.current) socketRef.current.disconnect(); };
  }, []);

  if (!stockData) {
    return (
      <div className="embed-stock-detail" style={{ background: bg }}>
        <div className="embed-loading">Loading...</div>
      </div>
    );
  }

  const isPositive = (stockData.changePercent24h || 0) >= 0;
  const changeColor = isPositive ? '#00ff88' : '#ff4444';
  const prices = chartData.flatMap(d => [d.high, d.low]).filter(p => p != null);
  const minPrice = prices.length > 0 ? Math.min(...prices) * 0.998 : stockData.currentPrice * 0.95;
  const maxPrice = prices.length > 0 ? Math.max(...prices) * 1.002 : stockData.currentPrice * 1.05;
  const chartHeight = 200;

  const candleData = chartData.map(entry => {
    const isUp = entry.close >= entry.open;
    return {
      ...entry,
      candleColor: isUp ? '#00ff88' : '#ff4444',
      wickHigh: entry.high, wickLow: entry.low,
      bodyTop: Math.max(entry.open, entry.close),
      bodyBottom: Math.min(entry.open, entry.close),
      bodyHeight: Math.max(Math.abs(entry.open - entry.close), 0.01),
    };
  });

  return (
    <div
      className={`embed-stock-detail layout-vertical ${fadeClass}`}
      style={{ background: bg === 'transparent' ? 'transparent' : bg, '--accent-color': '#00ff88', '--change-color': changeColor }}
    >
      <button className="embed-fullscreen-btn embed-fullscreen-corner" onClick={() => document.documentElement.requestFullscreen?.()}>&#x26F6;</button>

      {show.company && (
        <div className="stock-company-section">
          <div className="stock-ticker-large">{stockData.ticker}</div>
          <div className="stock-company-info">
            <span className="stock-name">{stockData.name}</span>
            {show.desc && stockData.description && (
              <span className="stock-description">{stockData.description}</span>
            )}
          </div>
        </div>
      )}

      {show.ceo && stockData.ceo_picture_url && (
        <div className="stock-ceo-section">
          <div className="ceo-picture-container">
            <img src={stockData.ceo_picture_url} alt="" className="ceo-picture" onError={(e) => { e.target.style.display = 'none'; }} />
          </div>
          <div className="ceo-info">
            <div className="ceo-title">Chief Executive Officer</div>
            <div className="ceo-name">{stockData.ceo_name || 'TBD'}</div>
            {show.desc && <div className="ceo-description">{stockData.ceo_description || ''}</div>}
          </div>
        </div>
      )}

      {show.chart && chartData.length > 0 && (
        <div className="stock-chart-container" style={{ height: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={candleData} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
              <XAxis dataKey="time" tick={{ fontSize: 9, fill: '#666' }} axisLine={{ stroke: '#333' }} tickLine={{ stroke: '#333' }} interval="preserveStartEnd" />
              <YAxis domain={[minPrice, maxPrice]} tick={{ fontSize: 9, fill: '#666' }} axisLine={{ stroke: '#333' }} tickLine={{ stroke: '#333' }} tickFormatter={v => formatCurrency(v)} orientation="right" width={55} />
              <ReferenceLine y={stockData.currentPrice} stroke={changeColor} strokeDasharray="3 3" strokeOpacity={0.5} />
              <Bar dataKey="bodyHeight" barSize={6} shape={(props) => {
                const { x, width, payload } = props;
                const scaledHeight = (payload.bodyHeight / (maxPrice - minPrice)) * (chartHeight - 20);
                return (
                  <g>
                    <line x1={x + width / 2} y1={(1 - (payload.wickHigh - minPrice) / (maxPrice - minPrice)) * (chartHeight - 20) + 10} x2={x + width / 2} y2={(1 - (payload.wickLow - minPrice) / (maxPrice - minPrice)) * (chartHeight - 20) + 10} stroke={payload.candleColor} strokeWidth={1} />
                    <rect x={x} y={(1 - (payload.bodyTop - minPrice) / (maxPrice - minPrice)) * (chartHeight - 20) + 10} width={width} height={Math.max(scaledHeight, 2)} fill={payload.candleColor} stroke={payload.candleColor} />
                  </g>
                );
              }}>
                {candleData.map((e, i) => <Cell key={i} fill={e.candleColor} />)}
              </Bar>
              <Line type="monotone" dataKey="close" stroke={changeColor} strokeWidth={1.5} dot={false} strokeOpacity={0.6} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {show.price && (
        <div className="stock-price-section">
          <div className="stock-current-price">{formatCurrency(stockData.currentPrice)}</div>
          <div className={`stock-change ${isPositive ? 'positive' : 'negative'}`}>
            <span className="change-arrow">{isPositive ? '▲' : '▼'}</span>
            <span className="change-percent">{isPositive ? '+' : ''}{stockData.changePercent24h?.toFixed(2)}%</span>
            <span className="change-amount">({isPositive ? '+' : ''}{formatCurrency(stockData.change24h)})</span>
          </div>
        </div>
      )}

      {show.stats && stockData.stats && (
        <div className="stock-stats-row">
          <div className="stat-item"><span className="stat-label">Open</span><span className="stat-value">{formatCurrency(stockData.basePrice)}</span></div>
          <div className="stat-item"><span className="stat-label">High</span><span className="stat-value positive">{formatCurrency(stockData.stats.dayHigh)}</span></div>
          <div className="stat-item"><span className="stat-label">Low</span><span className="stat-value negative">{formatCurrency(stockData.stats.dayLow)}</span></div>
          <div className="stat-item"><span className="stat-label">Vol</span><span className="stat-value">{stockData.stats.totalVolume?.toLocaleString() || '0'}</span></div>
        </div>
      )}
    </div>
  );
};

export default EmbedRandomDetail;
